import { Button } from "@components/ui/Button"
import { useButtonProps } from "@hooks/useButtonProps"
import { tv } from "@utils/tw"
import { defineBlock, EditableText, useInlineEditableValue } from "eddev/blocks"
import { Link } from "eddev/routing"

export const meta: BlockMeta = {
  title: "CTA Button Panel",
  icon: "forms",
  tags: ["#subpage"],
  flags: {
    contentWidth: true,
    marginRule: "long-card",
  },
  blockStyles: [
    {
      name: "default",
      label: "Default",
      isDefault: true,
    },
    {
      name: "disabled",
      label: "Disabled",
    },
    {
      name: "branded",
      label: "Branded",
    },
    {
      name: "brandedSubtle",
      label: "Branded Subtle",
    },
  ],
}

export default defineBlock("content/cta-button", (props) => {
  const styles = createStyles({ variant: props.blockStyle })
  const [title] = useInlineEditableValue<string>("title")

  const buttonProps = useButtonProps(props.link, {
    defaultVariant: "primary",
    primaryVariant: "primary",
  })

  return (
    <div className="@container" data-mixpanel-block="CTA Button" data-mixpanel-cta-title={title}>
      <Link
        href={props.blockStyle === "disabled" ? undefined : buttonProps.href}
        target={props.link?.target}
        className={styles.base()}
        onClick={(e) => {
          if (props.blockStyle === "disabled") {
            e.preventDefault()
            return
          }
          buttonProps.onClick?.(e as any)
        }}
      >
        <div className={styles.title()}>
          <EditableText
            as="div"
            store="title"
            className={props.blockStyle === "branded" ? "type-title-m" : "type-ui-l"}
            defaultValue={props.link?.title}
            placeholder="Enter a title"
          />
          {props.blockStyle != "branded" && (
            <EditableText
              as="div"
              store="description"
              className="type-body-s pt-2 md:pt-1"
              placeholder="Enter an optional description"
            />
          )}
        </div>
        <Button
          variant={props.blockStyle === "branded" || props.blockStyle === "brandedSubtle" ? "inverted" : "primary"}
          shape={props.blockStyle === "branded" || props.blockStyle === "brandedSubtle" ? "branded" : "default"}
          disabled={props.blockStyle === "disabled"}
          className="shrink-0"
        >
          <EditableText store="button" defaultValue={props.link?.title || "Read more"} />
        </Button>
      </Link>
    </div>
  )
})

const createStyles = tv({
  base: [
    "group flex flex-col gap-5 p-4 rounded-lg md:rounded-md text-center @lg:text-left",
    "bg-bg-mid/40 text-fg",
    "transition-colors",
    "@lg:flex-row @lg:items-center @lg:justify-between",
    "no-underline",
  ],
  slots: {
    title: "type-ui-l pl-2 text-pretty py-3 @lg:py-0",
    button: "",
  },
  variants: {
    variant: {
      default: {
        base: "hover:bg-bg-mid/60",
        button: "!bg-black text-white hover:bg-fg",
      },
      disabled: {
        base: "text-fg/60 cursor-default",
        button: "!bg-white/50 !opacity-100 !text-fg/40",
      },
      branded: {
        base: "bg-orange/90 text-black hover:bg-orange",
      },
      brandedSubtle: {
        base: "bg-orange/90 text-black hover:bg-orange",
      },
    },
  },
})
